import * as React from 'react';
import { useMemo, useState } from 'react';
import { DocumentViewer } from '../components/document-viewer';
import { DocumentRef, FindHint } from '../components/document-viewer/types';

export type PublishShell = 'lwr' | 'expenses';

export interface PublishPlaylistItem {
    id: string;
    label: string;
    url: string;
    amount?: number;
    dateLabel?: string;
    expenseTypeName?: string;
}

export type ExpenseFileStatus = 'pending' | 'approved' | 'review';

export interface PublishWorkflowFrameProps {
    shell: PublishShell;
    title: string;
    packetLabel?: string;
    lwrTabs?: Array<{ id: string; label: string }>;
    activeLwrTabId?: string;
    onLwrTabChange?: (id: string) => void;
    items: PublishPlaylistItem[];
    currentIndex: number;
    onIndexChange: (index: number) => void;
    onPopOut: () => void;
    onClose: () => void;
    onApprove?: () => void;
    onRequestReview?: () => void;
    statusNote?: string;
    findHints?: FindHint[];
    allowOcr?: boolean;
    verifyLabel?: string;
    autoSelectRequired?: boolean;
    onOrientedReplace?: (index: number, blob: Blob, fileName: string) => void;
}

const PublishWorkflowFrame = (props: PublishWorkflowFrameProps) => {
    const [expenseStatus, setExpenseStatus] = useState<{ [id: string]: ExpenseFileStatus }>({});

    const safeIndex = props.items.length
        ? Math.min(Math.max(props.currentIndex, 0), props.items.length - 1)
        : 0;
    const item = props.items[safeIndex];
    const total = props.items.length;
    const canPrev = total > 1 && safeIndex > 0;
    const canNext = total > 1 && safeIndex < total - 1;

    const documentRef: DocumentRef | undefined = item ? {
        docType: 0,
        lookupId: 0,
        label: props.title,
        fileIndex: safeIndex,
        totalFiles: Math.max(total, 1)
    } : undefined;

    const fileStatus: ExpenseFileStatus = item
        ? (expenseStatus[item.id] || 'pending')
        : 'pending';

    const statusUi = useMemo(() => {
        if (fileStatus === 'approved') {
            return { label: 'Approved', pill: 'dflPublishExpensePill dflPublishExpensePillOk', badge: 'dflPublishExpenseBadge dflPublishExpenseBadgeOk' };
        }
        if (fileStatus === 'review') {
            return { label: 'Pending Review', pill: 'dflPublishExpensePill dflPublishExpensePillReview', badge: 'dflPublishExpenseBadge dflPublishExpenseBadgeReview' };
        }
        return { label: 'Pending', pill: 'dflPublishExpensePill', badge: 'dflPublishExpenseBadge' };
    }, [fileStatus]);

    const stop = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const goDoc = (index: number) => {
        if (index < 0 || index >= total) {
            return;
        }
        if (props.shell === 'lwr' && props.lwrTabs && props.onLwrTabChange) {
            const tab = props.lwrTabs[index];
            if (tab) {
                props.onLwrTabChange(tab.id);
            }
        }
        props.onIndexChange(index);
    };

    const handleApprove = () => {
        if (!item) {
            return;
        }
        setExpenseStatus(prev => ({ ...prev, [item.id]: 'approved' }));
        if (props.onApprove) {
            props.onApprove();
        }
    };

    const handleReview = () => {
        if (!item) {
            return;
        }
        setExpenseStatus(prev => ({ ...prev, [item.id]: 'review' }));
        if (props.onRequestReview) {
            props.onRequestReview();
        }
    };

    return (
        <div className="dflPublishRoot">
            <button
                type="button"
                className="dflPublishBackdrop"
                aria-label="Exit publish"
                onClick={props.onClose}
            />
            <div
                className="dflPublishDrawer"
                role="dialog"
                aria-label="Workflow document preview"
                onClick={stop}
                onMouseDown={stop}
            >
                <div className="dflPublishInner">
                    <div className="dflPublishHeader">
                        <div className="dflPublishHeaderLeft">
                            <div className="dflPublishTitleBlock">
                                <h3 className="dflPublishTitle">{props.title}</h3>
                                {props.packetLabel && (
                                    <div className="dflPublishPacket">{props.packetLabel}</div>
                                )}
                            </div>
                            {props.shell === 'lwr' && props.lwrTabs && props.lwrTabs.length > 1 && (
                                <div className="dflPublishTabs">
                                    {props.lwrTabs.map(tab => (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            className={`dflPublishTab${props.activeLwrTabId === tab.id ? ' dflPublishTabOn' : ''}`}
                                            onClick={() => props.onLwrTabChange && props.onLwrTabChange(tab.id)}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {props.shell === 'expenses' && item && item.expenseTypeName && (
                                <div className={statusUi.pill}>
                                    <span>{item.expenseTypeName}</span>
                                    <span className={statusUi.badge}>{statusUi.label}</span>
                                </div>
                            )}
                        </div>

                        <div className="dflPublishHeaderRight">
                            {total > 0 && (
                                <div className="dflPublishNav">
                                    <button
                                        type="button"
                                        className="dflBtn"
                                        disabled={!canPrev}
                                        onClick={() => goDoc(safeIndex - 1)}
                                    >
                                        ‹
                                    </button>
                                    <span className="dflPublishFileMeta">
                                        {props.shell === 'lwr' ? 'Doc' : 'File'} {safeIndex + 1} of {total}
                                    </span>
                                    <button
                                        type="button"
                                        className="dflBtn"
                                        disabled={!canNext}
                                        onClick={() => goDoc(safeIndex + 1)}
                                    >
                                        ›
                                    </button>
                                </div>
                            )}
                            {props.shell === 'expenses' && item && item.dateLabel && (
                                <span className="dflPublishMuted">{item.dateLabel}</span>
                            )}
                            {props.shell === 'expenses' && item && item.amount != null && (
                                <span className="dflPublishAmount">
                                    ${Number(item.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                            )}
                            {props.shell === 'expenses' && (
                                <>
                                    <button
                                        type="button"
                                        className="dflBtnPrimary"
                                        disabled={!item}
                                        onClick={handleApprove}
                                        style={{ background: '#28a745' }}
                                    >
                                        Approve
                                    </button>
                                    <button
                                        type="button"
                                        className="dflBtn"
                                        disabled={!item}
                                        onClick={handleReview}
                                    >
                                        Request Review
                                    </button>
                                </>
                            )}
                            <button type="button" className="dflBtn" disabled={!item} onClick={props.onPopOut}>
                                Pop out
                            </button>
                            <button type="button" className="dflBtnPrimary" onClick={props.onClose}>
                                Exit Publish
                            </button>
                        </div>
                    </div>

                    {props.statusNote && <div className="dflPublishNote">{props.statusNote}</div>}

                    <div className="dflPublishViewerSlot">
                        {item ? (
                            <DocumentViewer
                                key={item.url}
                                url={item.url}
                                documentRef={documentRef}
                                height="100%"
                                showOcrStub={false}
                                showLocate={true}
                                findHints={props.findHints || []}
                                allowOcr={props.allowOcr === true}
                                allowRotate={true}
                                autoRunOcr={props.allowOcr === true}
                                fileName={item.label}
                                onOrientedSave={(blob, name) => {
                                    if (props.onOrientedReplace) {
                                        props.onOrientedReplace(safeIndex, blob, name);
                                    } else {
                                        const a = document.createElement('a');
                                        const href = URL.createObjectURL(blob);
                                        a.href = href;
                                        a.download = name;
                                        document.body.appendChild(a);
                                        a.click();
                                        setTimeout(() => {
                                            try { URL.revokeObjectURL(href); } catch { }
                                            try { a.remove(); } catch { }
                                        }, 1500);
                                    }
                                }}
                                verifyLabel={props.verifyLabel}
                                autoSelectRequired={props.autoSelectRequired === true}
                            />
                        ) : (
                            <div className="dflEmpty">Add documents to the playlist, then Publish.</div>
                        )}
                    </div>

                    <div className="dflPublishFooter">
                        <button type="button" className="dflBtnPrimary" onClick={props.onClose}>
                            Exit Publish
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PublishWorkflowFrame;
